import type { InventoryItemRow } from "~/@types/inventoryItemRow";
import type { TDateISO } from "~/@types/TDateISO";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type InventoryTab = "live-goods" | "hard-goods" | "availability" | "scheduled-transfers";

interface InventoryState {
	activeTab: InventoryTab;
	addItemDialogOpen: boolean;
	addItemIsHardGoods: boolean;
	adjustDialogOpen: boolean;
	adjustItem: InventoryItemRow | null;
	editMode: boolean;
	editReadyDateDialogOpen: boolean;
	editReadyDateItem: InventoryItemRow | null;
	gradeDialogOpen: boolean;
	gradeItem: InventoryItemRow | null;
	groupSheetItems: InventoryItemRow[];
	groupSheetKey: string;
	groupSheetOpen: boolean;
	itemsTableDialogOpen: boolean;
	moveDialogOpen: boolean;
	moveItems: InventoryItemRow[];
	reconcileDialogOpen: boolean;
	reconcileItems: InventoryItemRow[];
	scheduledTransferTombstones: Record<string, TDateISO>;
	scrapDialogOpen: boolean;
	scrapItems: InventoryItemRow[];
	selectedItem: InventoryItemRow | null;
	selectedItemId: string | null;
	transferDialogOpen: boolean;
	transferItem: InventoryItemRow | null;
}

const initialState: InventoryState = {
	activeTab: "live-goods",
	addItemDialogOpen: false,
	addItemIsHardGoods: false,
	adjustDialogOpen: false,
	adjustItem: null,
	editMode: false,
	editReadyDateDialogOpen: false,
	editReadyDateItem: null,
	gradeDialogOpen: false,
	gradeItem: null,
	groupSheetItems: [],
	groupSheetKey: "",
	groupSheetOpen: false,
	itemsTableDialogOpen: false,
	moveDialogOpen: false,
	moveItems: [],
	reconcileDialogOpen: false,
	reconcileItems: [],
	scheduledTransferTombstones: {},
	scrapDialogOpen: false,
	scrapItems: [],
	selectedItem: null,
	selectedItemId: null,
	transferDialogOpen: false,
	transferItem: null
};

export const inventorySlice = createSlice({
	initialState,
	name: "inventory",
	reducers: {
		closeAddItemDialog: (state) => {
			state.addItemDialogOpen = false;
			state.addItemIsHardGoods = false;
		},
		closeAdjustDialog: (state) => {
			state.adjustDialogOpen = false;
			state.adjustItem = null;
		},
		closeEditReadyDateDialog: (state) => {
			state.editReadyDateDialogOpen = false;
			state.editReadyDateItem = null;
		},
		closeGradeDialog: (state) => {
			state.gradeDialogOpen = false;
			state.gradeItem = null;
		},
		closeGroupSheet: (state) => {
			state.groupSheetOpen = false;
			state.groupSheetKey = "";
			state.groupSheetItems = [];
		},
		closeItemsTableDialog: (state) => {
			state.itemsTableDialogOpen = false;
		},
		closeMoveDialog: (state) => {
			state.moveDialogOpen = false;
			state.moveItems = [];
		},
		closeMoveDialogOnly: (state) => {
			state.moveDialogOpen = false;
		},
		closeReconcileDialog: (state) => {
			state.reconcileDialogOpen = false;
			state.reconcileItems = [];
		},
		closeScrapDialog: (state) => {
			state.scrapDialogOpen = false;
			state.scrapItems = [];
		},
		closeTransferDialog: (state) => {
			state.transferDialogOpen = false;
			state.transferItem = null;
		},
		openAddItemDialog: (state, action: PayloadAction<{ isHardGoods?: boolean } | undefined>) => {
			state.addItemDialogOpen = true;
			state.addItemIsHardGoods = action.payload?.isHardGoods ?? false;
		},
		openAdjustDialog: (state, action: PayloadAction<InventoryItemRow>) => {
			state.adjustDialogOpen = true;
			state.adjustItem = action.payload;
		},
		openEditReadyDateDialog: (state, action: PayloadAction<InventoryItemRow>) => {
			state.editReadyDateDialogOpen = true;
			state.editReadyDateItem = action.payload;
		},
		openGradeDialog: (state, action: PayloadAction<InventoryItemRow>) => {
			state.gradeDialogOpen = true;
			state.gradeItem = action.payload;
		},
		openGroupSheet: (state, action: PayloadAction<{ items: InventoryItemRow[]; key: string }>) => {
			state.groupSheetOpen = true;
			state.groupSheetKey = action.payload.key;
			state.groupSheetItems = action.payload.items;
		},
		openItemsTableDialog: (state) => {
			state.itemsTableDialogOpen = true;
		},
		openMoveDialog: (state) => {
			state.moveDialogOpen = true;
		},
		openMoveDialogWithItems: (state, action: PayloadAction<InventoryItemRow[]>) => {
			state.moveDialogOpen = true;
			state.moveItems = action.payload;
		},
		openReconcileDialogWithItems: (state, action: PayloadAction<InventoryItemRow[]>) => {
			state.reconcileDialogOpen = true;
			state.reconcileItems = action.payload;
		},
		openScrapDialog: (state) => {
			state.scrapDialogOpen = true;
		},
		openScrapDialogWithItems: (state, action: PayloadAction<InventoryItemRow[]>) => {
			state.scrapDialogOpen = true;
			state.scrapItems = action.payload;
		},
		openTransferDialog: (state, action: PayloadAction<InventoryItemRow>) => {
			state.transferDialogOpen = true;
			state.transferItem = action.payload;
		},
		releaseScheduledTransferTombstones: (state, action: PayloadAction<string[]>) => {
			for (const scheduledInventoryTransferId of action.payload) {
				delete state.scheduledTransferTombstones[scheduledInventoryTransferId];
			}
		},
		setActiveTab: (state, action: PayloadAction<InventoryTab>) => {
			state.activeTab = action.payload;
		},
		setEditMode: (state, action: PayloadAction<boolean>) => {
			state.editMode = action.payload;
		},
		setSelectedItem: (state, action: PayloadAction<InventoryItemRow | null>) => {
			state.selectedItem = action.payload;
			state.selectedItemId = action.payload?.id ?? null;
		},
		setSelectedItemId: (state, action: PayloadAction<string | null>) => {
			state.selectedItemId = action.payload;
			if (!action.payload) state.selectedItem = null;
		},
		tombstoneScheduledTransfer: (
			state,
			action: PayloadAction<{ cancelledAt: TDateISO; scheduledInventoryTransferId: string }>
		) => {
			state.scheduledTransferTombstones[action.payload.scheduledInventoryTransferId] = action.payload.cancelledAt;
		},
		updateSelectedItem: (state, action: PayloadAction<Partial<InventoryItemRow>>) => {
			if (state.selectedItem) {
				state.selectedItem = { ...state.selectedItem, ...action.payload };
			}
		}
	}
});

export const {
	closeAddItemDialog,
	closeAdjustDialog,
	closeEditReadyDateDialog,
	closeGradeDialog,
	closeGroupSheet,
	closeItemsTableDialog,
	closeMoveDialog,
	closeMoveDialogOnly,
	closeReconcileDialog,
	closeScrapDialog,
	closeTransferDialog,
	openAddItemDialog,
	openAdjustDialog,
	openEditReadyDateDialog,
	openGradeDialog,
	openGroupSheet,
	openItemsTableDialog,
	openMoveDialog,
	openMoveDialogWithItems,
	openReconcileDialogWithItems,
	openScrapDialog,
	openScrapDialogWithItems,
	openTransferDialog,
	releaseScheduledTransferTombstones,
	setActiveTab,
	setEditMode,
	setSelectedItem,
	setSelectedItemId,
	tombstoneScheduledTransfer,
	updateSelectedItem
} = inventorySlice.actions;

export default inventorySlice.reducer;
